import { gql } from '@apollo/client'

// ==========================================
// MENU QUERIES
// ==========================================

export const GET_ADVANCED_MENUS = gql`
  query GetAdvancedMenus($restaurantId: ID!) {
    advancedMenus(restaurantId: $restaurantId) {
      _id
      restaurantId
      name
      description
      menuType
      startTime
      endTime
      daysOfWeek
      daySchedule
      isActive
      displayOrder
      isCatering
      markupPercent
      roundingMode
      roundingValue
      categoryOverrides
      itemOverrides
      items {
        _id
        name
        categoryName
        pricingMode
        basePrice
        variantPrices
        markedUpPrice
        markedUpVariantPrices
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_ADVANCED_MENU = gql`
  query GetAdvancedMenu($id: ID!) {
    advancedMenu(id: $id) {
      _id
      restaurantId
      name
      description
      menuType
      startTime
      endTime
      daysOfWeek
      daySchedule
      isActive
      displayOrder
      markupPercent
      roundingMode
      roundingValue
      categoryOverrides
      itemOverrides
      items {
        _id
        name
        description
        pricingMode
        basePrice
        variantPrices
        markedUpPrice
        markedUpVariantPrices
      }
      createdAt
      updatedAt
    }
  }
`

// ==========================================
// ITEM QUERIES
// ==========================================

export const GET_RESTAURANT_ITEMS = gql`
  query GetRestaurantItems($restaurantId: ID!) {
    advancedRestaurantItems(restaurantId: $restaurantId) {
      _id
      restaurantId
      name
      alternateName1
      alternateName2
      description
      imageUrl
      aiImageUrl
      aiDescription
      categoryName
      subcategoryName
      pricingMode
      basePrice
      variantPrices
      taxOption
      dietaryTags
      allergenTags
      isActive
      isAvailable
      isFeatured
      is86ed
      eightysixReason
      cateringItemId
      serves
      variantServes
      modifierGroups {
        _id
        name
        selectionType
        isRequired
        modifiers {
          _id
          modifier { _id name }
          item { _id name }
          priceOverrideMode
          fixedPrice
        }
      }
      optionGroups {
        _id
        name
        choices
        selectionType
        isRequired
      }
      criticalIngredients {
        _id
        name
      }
      criticalIngredientItems {
        _id
        name
        is86ed
      }
      createdAt
    }
  }
`

export const GET_ADVANCED_MENU_ITEMS = gql`
  query GetAdvancedMenuItems($menuId: ID!) {
    advancedMenuItems(menuId: $menuId) {
      _id
      menuId
      restaurantId
      name
      description
      imageUrl
      categoryName
      subcategoryName
      pricingMode
      basePrice
      variantPrices
      markedUpPrice
      markedUpVariantPrices
      prepTimeMinutes
      calories
      dietaryTags
      allergenTags
      isActive
      isAvailable
      is86ed
      displayOrder
      isFeatured
      cateringItemId
      serves
      variantServes
      modifierGroups {
        _id
        name
      }
      optionGroups {
        _id
        name
        choices
        selectionType
        isRequired
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_ADVANCED_MENU_ITEM = gql`
  query GetAdvancedMenuItem($id: ID!) {
    advancedMenuItem(id: $id) {
      _id
      menuId
      restaurantId
      name
      description
      imageUrl
      categoryName
      pricingMode
      basePrice
      variantPrices
      is86ed
      cateringItemId
      serves
      variantServes
      createdAt
      updatedAt
    }
  }
`

// ==========================================
// MODIFIER QUERIES
// ==========================================

export const GET_ADVANCED_MODIFIERS = gql`
  query GetAdvancedModifiers($restaurantId: ID!) {
    advancedModifiers(restaurantId: $restaurantId) {
      _id
      restaurantId
      name
      description
      modifierType
      isIngredient
      isModifier
      libraryStatus {
        is86ed
        eightysixReason
        eightysixUntil
        usageCount
        usageGroups
        stockLevel
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_ADVANCED_MODIFIER_LIBRARY = gql`
  query GetAdvancedModifierLibrary($restaurantId: ID!) {
    advancedModifierLibrary(restaurantId: $restaurantId) {
      _id
      name
      libraryStatus {
        is86ed
        usageCount
      }
    }
  }
`

// ==========================================
// MODIFIER GROUP QUERIES
// ==========================================

export const GET_ADVANCED_MODIFIER_GROUPS = gql`
  query GetAdvancedModifierGroups($restaurantId: ID!) {
    advancedModifierGroups(restaurantId: $restaurantId) {
      _id
      restaurantId
      name
      description
      isTemplate
      selectionType
      minSelections
      maxSelections
      exactSelections
      isRequired
      groupRequirement
      additionalCost
      allowDuplicates
      maxDuplicates
      duplicatePricingMode
      duplicatePrices
      displayOrder
      modifiers {
        _id
        memberType
        modifier {
          _id
          name
          modifierType
        }
        item {
          _id
          name
          pricingMode
          basePrice
          variantPrices
          modifierGroups {
            _id
            name
            selectionType
            isRequired
            modifiers {
              _id
              modifier { _id name }
              item { _id name }
              priceOverrideMode
              fixedPrice
            }
          }
        }
        priceOverrideMode
        fixedPrice
        variantPrices
        displayOrder
        isDefault
        isHidden
        nestedGroups
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_ADVANCED_MODIFIER_GROUP = gql`
  query GetAdvancedModifierGroup($id: ID!) {
    advancedModifierGroup(id: $id) {
      _id
      name
      description
      selectionType
      minSelections
      maxSelections
      isRequired
      allowDuplicates
      maxDuplicates
      duplicatePricingMode
      duplicatePrices
      modifiers {
        _id
        modifier {
          _id
          name
        }
        priceOverrideMode
        fixedPrice
        variantPrices
        displayOrder
      }
    }
  }
`

export const GET_ADVANCED_TEMPLATE_GROUPS = gql`
  query GetAdvancedTemplateGroups($restaurantId: ID!) {
    advancedTemplateGroups(restaurantId: $restaurantId) {
      _id
      name
      description
      selectionType
      isTemplate
    }
  }
`

export const GET_CATEGORY_MODIFIER_GROUPS = gql`
  query GetCategoryModifierGroups($restaurantId: ID!, $categoryName: String!) {
    categoryModifierGroups(restaurantId: $restaurantId, categoryName: $categoryName)
  }
`

// ==========================================
// OPTION GROUP QUERIES
// ==========================================

export const GET_ADVANCED_OPTION_GROUPS = gql`
  query GetAdvancedOptionGroups($restaurantId: ID!) {
    advancedOptionGroups(restaurantId: $restaurantId) {
      _id
      restaurantId
      name
      choices
      selectionType
      isRequired
      displayOrder
      itemCount
      createdAt
      updatedAt
    }
  }
`

// ==========================================
// 86 QUERIES
// ==========================================

export const GET_EIGHTYSIXED_MODIFIERS = gql`
  query GetEightySixedModifiers($restaurantId: ID!) {
    eightySixedModifiers(restaurantId: $restaurantId) {
      _id
      name
      libraryStatus {
        is86ed
        eightysixReason
        eightysixUntil
      }
    }
  }
`

export const GET_EIGHTYSIXED_ITEMS = gql`
  query GetEightySixedItems($restaurantId: ID!) {
    eightySixedItems(restaurantId: $restaurantId) {
      _id
      name
      is86ed
      eightysixReason
      eightysixUntil
    }
  }
`

// ==========================================
// UPLOAD QUERIES
// ==========================================

export const GET_ADVANCED_MENU_UPLOADS = gql`
  query GetAdvancedMenuUploads($restaurantId: ID!) {
    advancedMenuUploads(restaurantId: $restaurantId) {
      _id
      restaurantId
      uploadMethod
      originalFilename
      fileUrl
      aiProcessingStatus
      itemsImported
      itemsFailed
      createdAt
      completedAt
    }
  }
`

export const GET_UPLOAD_STATUS = gql`
  query GetUploadStatus($uploadId: ID!) {
    advancedMenuUploadStatus(uploadId: $uploadId) {
      _id
      aiProcessingStatus
      itemsImported
      itemsFailed
      progressMessage
      completedAt
    }
  }
`


// ==========================================
// 86 MANAGEMENT QUERIES
// ==========================================

export const GET_EIGHTY_SIX_DASHBOARD = gql`
  query GetEightySixDashboard($restaurantId: ID!) {
    eightySixDashboard(restaurantId: $restaurantId) {
      modifiers {
        id
        name
        type
        reason
        until
        since
      }
      items {
        id
        name
        categoryName
        reason
        until
        since
      }
      totalCount
    }
  }
`

export const GET_CASCADE_PREVIEW = gql`
  query GetCascadePreview($modifierId: ID!) {
    cascadePreview(modifierId: $modifierId) {
      modifierId
      modifierName
      modifierType
      affectedItemCount
      affectedItems {
        id
        name
        categoryName
        menuId
        currentlyAvailable
        currently86ed
      }
      wouldDisable
    }
  }
`
